/**
 * TanStack Query hooks для Audit Log API (P1.6 — Audit Log + Run History).
 *
 * - useAuditLogs(filters) — list own audit logs (GET /audit-logs)
 * - useAuditLog(id)       — single entry (GET /audit-logs/{id})
 *
 * Фильтры прокидываются как query-параметры; undefined/null поля опускаются.
 * `from`/`to` — ISO datetime; бэкенд ожидает ключи `from`/`to` (alias для
 * date_from). `page`/`page_size` — серверная пагинация (по умолчанию 1 / 50).
 */
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import api from '@/lib/api';

export interface AuditLogItem {
  id: string;
  account_id: string;
  actor_account_id: string | null;
  action: string;
  project_id: string | null;
  resource_type: string | null;
  resource_id: string | null;
  details: Record<string, unknown> | null;
  ip_address: string | null;
  user_agent: string | null;
  description: string | null;
  created_at: string;
}

export interface AuditLogListResponse {
  items: AuditLogItem[];
  total: number;
}

export interface AuditLogFilters {
  project_id?: string | null;
  action?: string | null;
  resource_type?: string | null;
  resource_id?: string | null;
  actor_account_id?: string | null;
  from?: string | null;
  to?: string | null;
  search?: string | null;
  page?: number;
  page_size?: number;
}

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 50;

/** Собирает query-параметры из filters, опуская undefined/null/пустые. */
function buildParams(filters: AuditLogFilters): Record<string, string> {
  const params: Record<string, string> = {};
  const page = filters.page ?? DEFAULT_PAGE;
  const pageSize = filters.page_size ?? DEFAULT_PAGE_SIZE;
  params.page = String(page);
  params.page_size = String(pageSize);

  if (filters.project_id) params.project_id = filters.project_id;
  if (filters.action) params.action = filters.action;
  if (filters.resource_type) params.resource_type = filters.resource_type;
  if (filters.resource_id) params.resource_id = filters.resource_id;
  if (filters.actor_account_id) params.actor_account_id = filters.actor_account_id;
  if (filters.from) params.from = filters.from;
  if (filters.to) params.to = filters.to;
  if (filters.search) params.search = filters.search;
  return params;
}

export function useAuditLogs(
  filters: AuditLogFilters,
  options?: Partial<UseQueryOptions<AuditLogListResponse>>,
) {
  return useQuery<AuditLogListResponse>({
    queryKey: ['audit-logs', filters],
    queryFn: async () =>
      (
        await api.get<AuditLogListResponse>('/audit-logs', {
          params: buildParams(filters),
        })
      ).data,
    ...options,
  });
}

export function useAuditLog(
  id: string | null,
  options?: Partial<UseQueryOptions<AuditLogItem>>,
) {
  return useQuery<AuditLogItem>({
    queryKey: ['audit-logs', id],
    queryFn: async () => (await api.get<AuditLogItem>(`/audit-logs/${id}`)).data,
    enabled: Boolean(id),
    ...options,
  });
}